import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    Users,
    FileText,
    Search,
    RefreshCw,
    KeyRound,
    UserX,
    UserCheck,
    Trash2,
    ChevronLeft,
    ChevronRight,
    Clock,
    Mail,
    Shield,
    ShieldOff,
    Activity,
    Eye,
    Copy,
    Check,
    AlertTriangle,
    Crown,
    Star,
    Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tier configuration
const TIER_CONFIG = {
    free: { name: 'Free', icon: Star, color: 'zinc', description: '5 blueprints/day' },
    pro: { name: 'Pro', icon: Zap, color: 'blue', description: '25 blueprints/day' },
    unlimited: { name: 'Unlimited', icon: Crown, color: 'yellow', description: 'Unlimited' }
};

// Base Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
    if (!isOpen) return null;
    
    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-2xl',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl'
    };
    
    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 overflow-y-auto"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.95 }}
                    className={`${sizeClasses[size]} w-full bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl my-8`}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                        <h2 className="text-lg font-semibold text-white">{title}</h2>
                        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                    <div className="p-4 max-h-[70vh] overflow-y-auto">
                        {children}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

// Users List Modal
export const UsersModal = ({ isOpen, onClose, getAuthHeaders }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 15 });
            if (search) params.append('search', search);
            if (roleFilter !== 'all') params.append('role', roleFilter);
            if (statusFilter !== 'all') params.append('status', statusFilter);
            
            const res = await axios.get(`${API}/admin/users?${params}`, { headers: getAuthHeaders() });
            setUsers(res.data.users);
            setTotalPages(res.data.total_pages);
        } catch (err) {
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        if (isOpen) fetchUsers();
    }, [isOpen, page, roleFilter, statusFilter]);
    
    useEffect(() => {
        const timer = setTimeout(() => {
            if (isOpen) fetchUsers();
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);
    
    if (selectedUser) {
        return (
            <UserDetailModal
                isOpen={isOpen}
                onClose={onClose}
                userId={selectedUser}
                onBack={() => setSelectedUser(null)}
                getAuthHeaders={getAuthHeaders}
                onUserUpdated={fetchUsers}
            />
        );
    }
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="User Management" size="lg">
            {/* Search & Filters */}
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                        placeholder="Search by email or name..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9 bg-zinc-800 border-zinc-700"
                    />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-full sm:w-32 bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="user">User</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-32 bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="deactivated">Deactivated</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            
            {/* Users Table */}
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-zinc-800">
                                    <th className="text-left py-2 px-3 text-zinc-400">User</th>
                                    <th className="text-left py-2 px-3 text-zinc-400 hidden sm:table-cell">Role/Tier</th>
                                    <th className="text-left py-2 px-3 text-zinc-400 hidden md:table-cell">Blueprints</th>
                                    <th className="text-left py-2 px-3 text-zinc-400 hidden md:table-cell">Last Active</th>
                                    <th className="text-right py-2 px-3 text-zinc-400">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => {
                                    const tierConfig = TIER_CONFIG[user.tier] || TIER_CONFIG.free;
                                    const TierIcon = tierConfig.icon;
                                    return (
                                    <tr key={user.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                                        <td className="py-2 px-3">
                                            <div>
                                                <p className="text-white font-medium truncate max-w-[150px] sm:max-w-none">{user.email}</p>
                                                <p className="text-xs text-zinc-500">{user.name || 'No name'}</p>
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 hidden sm:table-cell">
                                            <div className="flex flex-wrap gap-1">
                                                <span className={`px-2 py-0.5 rounded text-xs ${
                                                    user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-zinc-700 text-zinc-300'
                                                }`}>
                                                    {user.role}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 bg-${tierConfig.color}-500/20 text-${tierConfig.color}-400`}>
                                                    <TierIcon className="w-3 h-3" />
                                                    {tierConfig.name}
                                                </span>
                                                {!user.is_active && (
                                                    <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">
                                                        inactive
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 text-zinc-300 hidden md:table-cell">{user.blueprint_count}</td>
                                        <td className="py-2 px-3 text-zinc-400 text-xs hidden md:table-cell">
                                            {user.last_activity ? new Date(user.last_activity).toLocaleDateString() : 'Never'}
                                        </td>
                                        <td className="py-2 px-3 text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setSelectedUser(user.id)}
                                                className="h-7 text-xs"
                                            >
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                        <span className="text-sm text-zinc-400">Page {page} of {totalPages}</span>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="h-8"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="h-8"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
};

// User Detail Modal
export const UserDetailModal = ({ isOpen, onClose, userId, onBack, getAuthHeaders, onUserUpdated }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [tempPassword, setTempPassword] = useState(null);
    const [copied, setCopied] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    
    const fetchUser = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
            setUser(res.data);
        } catch (err) {
            toast.error('Failed to load user details');
            onBack();
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        if (isOpen && userId) fetchUser();
    }, [isOpen, userId]);
    
    const handleResetPassword = async () => {
        setActionLoading('password');
        try {
            const res = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, { headers: getAuthHeaders() });
            setTempPassword(res.data.temporary_password);
            toast.success('Password reset successfully');
        } catch (err) {
            toast.error('Failed to reset password');
        } finally {
            setActionLoading(null);
        }
    };
    
    const handleToggleActive = async () => {
        const isActive = user?.user?.is_active !== false;
        const endpoint = isActive ? 'deactivate' : 'activate';
        setActionLoading('status');
        try {
            await axios.patch(`${API}/admin/users/${userId}/${endpoint}`, {}, { headers: getAuthHeaders() });
            toast.success(`User ${isActive ? 'deactivated' : 'activated'}`);
            fetchUser();
            onUserUpdated?.();
        } catch (err) {
            toast.error(`Failed to ${endpoint} user`);
        } finally {
            setActionLoading(null);
        }
    };
    
    const handleChangeRole = async () => {
        const currentRole = user?.user?.role;
        const newRole = currentRole === 'admin' ? 'user' : 'admin';
        setActionLoading('role');
        try {
            await axios.patch(`${API}/admin/users/${userId}/role?new_role=${newRole}`, {}, { headers: getAuthHeaders() });
            toast.success(`User role changed to ${newRole}`);
            fetchUser();
            onUserUpdated?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to change role');
        } finally {
            setActionLoading(null);
        }
    };
    
    const handleChangeTier = async (newTier) => {
        setActionLoading('tier');
        try {
            await axios.patch(`${API}/admin/users/${userId}/tier?new_tier=${newTier}`, {}, { headers: getAuthHeaders() });
            toast.success(`User tier changed to ${TIER_CONFIG[newTier]?.name || newTier}`);
            fetchUser();
            onUserUpdated?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to change tier');
        } finally {
            setActionLoading(null);
        }
    };
    
    const handleDelete = async () => {
        setActionLoading('delete');
        try {
            await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
            toast.success('User deleted');
            onUserUpdated?.();
            onBack();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete user');
        } finally {
            setActionLoading(null);
        }
    };
    
    const copyPassword = () => {
        navigator.clipboard.writeText(tempPassword);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="User Details" size="lg">
            <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Users
            </Button>
            
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : user ? (
                <div className="space-y-6">
                    {/* User Info */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-zinc-800/50 rounded-lg">
                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                            <Users className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-white truncate">{user.user.email}</h3>
                            <p className="text-sm text-zinc-400">{user.user.name || 'No name set'}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                    user.user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-zinc-700 text-zinc-300'
                                }`}>
                                    {user.user.role}
                                </span>
                                {(() => {
                                    const tierKey = user.user.tier || 'free';
                                    const tierCfg = TIER_CONFIG[tierKey] || TIER_CONFIG.free;
                                    const TIcon = tierCfg.icon;
                                    return (
                                        <span className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 bg-${tierCfg.color}-500/20 text-${tierCfg.color}-400`}>
                                            <TIcon className="w-3 h-3" />
                                            {tierCfg.name}
                                        </span>
                                    );
                                })()}
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                    user.user.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                }`}>
                                    {user.user.is_active !== false ? 'Active' : 'Deactivated'}
                                </span>
                            </div>
                        </div>
                        <div className="text-sm text-zinc-400">
                            Joined: {new Date(user.user.created_at).toLocaleDateString()}
                        </div>
                    </div>
                    
                    {/* Stats - Clear, Distinct Metrics */}
                    <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                            <div className="p-3 bg-zinc-800/50 rounded-lg text-center" title={user.stats_definitions?.saved_blueprints}>
                                <p className="text-2xl font-bold text-cyan-400">{user.stats.saved_blueprints || 0}</p>
                                <p className="text-xs text-zinc-400">Saved</p>
                            </div>
                            <div className="p-3 bg-zinc-800/50 rounded-lg text-center" title={user.stats_definitions?.shared_blueprints}>
                                <p className="text-2xl font-bold text-purple-400">{user.stats.shared_blueprints || 0}</p>
                                <p className="text-xs text-zinc-400">Shared</p>
                            </div>
                            <div className="p-3 bg-zinc-800/50 rounded-lg text-center" title={user.stats_definitions?.blueprint_events}>
                                <p className="text-2xl font-bold text-yellow-400">{user.stats.blueprint_events || 0}</p>
                                <p className="text-xs text-zinc-400">Gen Events</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="p-2 bg-zinc-800/30 rounded-lg text-center" title={user.stats_definitions?.linked_sessions}>
                                <p className="text-lg font-bold text-blue-400">{user.stats.linked_sessions || 0}</p>
                                <p className="text-[10px] text-zinc-500">Sessions</p>
                            </div>
                            <div className="p-2 bg-zinc-800/30 rounded-lg text-center" title={user.stats_definitions?.recent_events}>
                                <p className="text-lg font-bold text-zinc-400">{user.stats.recent_events || 0}</p>
                                <p className="text-[10px] text-zinc-500">Events</p>
                            </div>
                        </div>
                        <p className="text-[10px] text-zinc-600 text-center">
                            Hover over stats for definitions. Saved + Shared = persisted blueprints. Gen Events = all generation attempts.
                        </p>
                    </div>
                    
                    {/* Tier Management */}
                    <div className="p-4 bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-500/30 rounded-lg">
                        <h4 className="text-sm font-medium text-yellow-400 flex items-center gap-2 mb-3">
                            <Crown className="w-4 h-4" />
                            Subscription Tier
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                            {Object.entries(TIER_CONFIG).map(([key, config]) => {
                                const TierIcon = config.icon;
                                const isCurrentTier = (user.user.tier || 'free') === key;
                                return (
                                    <button
                                        key={key}
                                        onClick={() => !isCurrentTier && handleChangeTier(key)}
                                        disabled={actionLoading === 'tier' || isCurrentTier}
                                        className={`p-3 rounded-lg border transition-all ${
                                            isCurrentTier 
                                                ? `bg-${config.color}-500/20 border-${config.color}-500 ring-2 ring-${config.color}-500/50` 
                                                : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-500'
                                        } ${actionLoading === 'tier' ? 'opacity-50' : ''}`}
                                    >
                                        <TierIcon className={`w-5 h-5 mx-auto mb-1 ${isCurrentTier ? `text-${config.color}-400` : 'text-zinc-400'}`} />
                                        <p className={`text-sm font-medium ${isCurrentTier ? `text-${config.color}-400` : 'text-zinc-300'}`}>
                                            {config.name}
                                        </p>
                                        <p className="text-[10px] text-zinc-500">{config.description}</p>
                                        {isCurrentTier && <p className="text-[10px] text-green-400 mt-1">Current</p>}
                                    </button>
                                );
                            })}
                        </div>
                        {user.user.tier_updated_at && (
                            <p className="text-[10px] text-zinc-500 mt-2">
                                Last changed: {new Date(user.user.tier_updated_at).toLocaleDateString()} 
                                {user.user.tier_updated_by && ` by ${user.user.tier_updated_by}`}
                            </p>
                        )}
                    </div>
                    
                    {/* Actions */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-medium text-zinc-300">Actions</h4>
                        
                        {/* Password Reset */}
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-medium text-white flex items-center gap-2">
                                        <KeyRound className="w-4 h-4 text-yellow-400" />
                                        Reset Password
                                    </p>
                                    <p className="text-xs text-zinc-400 mt-1">Generate a temporary password for this user</p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleResetPassword}
                                    disabled={actionLoading === 'password'}
                                    className="shrink-0"
                                >
                                    {actionLoading === 'password' ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Reset'}
                                </Button>
                            </div>
                            {tempPassword && (
                                <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded flex items-center justify-between">
                                    <code className="text-yellow-400 text-sm">{tempPassword}</code>
                                    <Button variant="ghost" size="sm" onClick={copyPassword} className="h-7">
                                        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                    </Button>
                                </div>
                            )}
                        </div>
                        
                        {/* Toggle Role */}
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-medium text-white flex items-center gap-2">
                                        {user.user.role === 'admin' ? (
                                            <ShieldOff className="w-4 h-4 text-purple-400" />
                                        ) : (
                                            <Shield className="w-4 h-4 text-purple-400" />
                                        )}
                                        {user.user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                                    </p>
                                    <p className="text-xs text-zinc-400 mt-1">
                                        {user.user.role === 'admin' ? 'Demote to regular user' : 'Grant admin privileges'}
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleChangeRole}
                                    disabled={actionLoading === 'role'}
                                    className="shrink-0"
                                >
                                    {actionLoading === 'role' ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Change'}
                                </Button>
                            </div>
                        </div>
                        
                        {/* Toggle Active */}
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-medium text-white flex items-center gap-2">
                                        {user.user.is_active !== false ? (
                                            <UserX className="w-4 h-4 text-orange-400" />
                                        ) : (
                                            <UserCheck className="w-4 h-4 text-green-400" />
                                        )}
                                        {user.user.is_active !== false ? 'Deactivate Account' : 'Activate Account'}
                                    </p>
                                    <p className="text-xs text-zinc-400 mt-1">
                                        {user.user.is_active !== false ? 'Prevent user from logging in' : 'Allow user to log in again'}
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleToggleActive}
                                    disabled={actionLoading === 'status'}
                                    className="shrink-0"
                                >
                                    {actionLoading === 'status' ? <RefreshCw className="w-4 h-4 animate-spin" /> : (user.user.is_active !== false ? 'Deactivate' : 'Activate')}
                                </Button>
                            </div>
                        </div>
                        
                        {/* Delete */}
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            {!showDeleteConfirm ? (
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-red-400 flex items-center gap-2">
                                            <Trash2 className="w-4 h-4" />
                                            Delete User
                                        </p>
                                        <p className="text-xs text-zinc-400 mt-1">Permanently delete this user and all their data</p>
                                    </div>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="shrink-0"
                                    >
                                        Delete
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <p className="text-sm text-red-400 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        Are you sure? This cannot be undone.
                                    </p>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setShowDeleteConfirm(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={handleDelete}
                                            disabled={actionLoading === 'delete'}
                                        >
                                            {actionLoading === 'delete' ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Yes, Delete'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Recent Activity */}
                    {user.recent_activity && user.recent_activity.length > 0 && (
                        <div>
                            <h4 className="text-sm font-medium text-zinc-300 mb-3">Recent Activity</h4>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                {user.recent_activity.map((event, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-2 bg-zinc-800/30 rounded text-xs">
                                        <Activity className="w-3 h-3 text-zinc-500 shrink-0" />
                                        <span className="text-zinc-300">{event.event_type}</span>
                                        <span className="text-zinc-500 ml-auto">
                                            {new Date(event.timestamp).toLocaleString()}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : null}
        </Modal>
    );
};

// Sessions Drill-Down Modal
export const SessionsModal = ({ isOpen, onClose, getAuthHeaders, initialFilter }) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sessionType, setSessionType] = useState(initialFilter || 'all');
    const [hasBlueprint, setHasBlueprint] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedSession, setSelectedSession] = useState(null);
    
    const fetchSessions = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 15 });
            if (sessionType !== 'all') params.append('session_type', sessionType);
            if (hasBlueprint !== 'all') params.append('has_blueprint', hasBlueprint === 'yes');
            
            const res = await axios.get(`${API}/admin/sessions?${params}`, { headers: getAuthHeaders() });
            setSessions(res.data.sessions);
            setTotalPages(res.data.total_pages);
        } catch (err) {
            toast.error('Failed to load sessions');
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        if (isOpen) fetchSessions();
    }, [isOpen, page, sessionType, hasBlueprint]);
    
    if (selectedSession) {
        return (
            <SessionDetailModal
                isOpen={isOpen}
                onClose={onClose}
                sessionId={selectedSession}
                onBack={() => setSelectedSession(null)}
                getAuthHeaders={getAuthHeaders}
            />
        );
    }
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Session Details" size="lg">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
                <Select value={sessionType} onValueChange={setSessionType}>
                    <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Session Type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Sessions</SelectItem>
                        <SelectItem value="anonymous">Anonymous</SelectItem>
                        <SelectItem value="registered">Registered</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={hasBlueprint} onValueChange={setHasBlueprint}>
                    <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Blueprint" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="yes">With Blueprint</SelectItem>
                        <SelectItem value="no">Without Blueprint</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : (
                <>
                    <div className="space-y-2">
                        {sessions.map(session => (
                            <div
                                key={session.session_id}
                                className="p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 cursor-pointer transition-colors"
                                onClick={() => setSelectedSession(session.session_id)}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm text-white font-mono truncate">{session.session_id}</p>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                            <span className={`px-2 py-0.5 rounded text-xs ${
                                                session.session_type === 'registered' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                                            }`}>
                                                {session.session_type}
                                            </span>
                                            {session.has_blueprint && (
                                                <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
                                                    {session.blueprint_count} blueprint{session.blueprint_count !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                            <span className="text-xs text-zinc-500">
                                                {session.event_count} events
                                            </span>
                                        </div>
                                        {session.user_email && (
                                            <p className="text-xs text-zinc-400 mt-1">{session.user_email}</p>
                                        )}
                                    </div>
                                    <div className="text-xs text-zinc-500 text-right shrink-0">
                                        <p>{new Date(session.first_event).toLocaleDateString()}</p>
                                        <p>{new Date(session.first_event).toLocaleTimeString()}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                        <span className="text-sm text-zinc-400">Page {page} of {totalPages}</span>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
};

// Session Detail Modal
const SessionDetailModal = ({ isOpen, onClose, sessionId, onBack, getAuthHeaders }) => {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchSession = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/admin/sessions/${sessionId}`, { headers: getAuthHeaders() });
                setSession(res.data);
            } catch (err) {
                toast.error('Failed to load session details');
                onBack();
            } finally {
                setLoading(false);
            }
        };
        
        if (isOpen && sessionId) fetchSession();
    }, [isOpen, sessionId]);
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Session Events" size="lg">
            <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Sessions
            </Button>
            
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : session ? (
                <div className="space-y-4">
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                        <p className="text-xs text-zinc-400">Session ID</p>
                        <p className="text-sm font-mono text-white break-all">{session.session_id}</p>
                        {session.user && (
                            <p className="text-xs text-zinc-400 mt-2">User: {session.user.email}</p>
                        )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                        <div className="p-2 bg-zinc-800/50 rounded text-center">
                            <p className="text-lg font-bold text-white">{session.event_count}</p>
                            <p className="text-xs text-zinc-400">Events</p>
                        </div>
                        <div className="p-2 bg-zinc-800/50 rounded text-center">
                            <p className="text-xs text-white">{new Date(session.first_event).toLocaleString()}</p>
                            <p className="text-xs text-zinc-400">First Event</p>
                        </div>
                        <div className="p-2 bg-zinc-800/50 rounded text-center">
                            <p className="text-xs text-white">{new Date(session.last_event).toLocaleString()}</p>
                            <p className="text-xs text-zinc-400">Last Event</p>
                        </div>
                    </div>
                    
                    <div>
                        <h4 className="text-sm font-medium text-zinc-300 mb-2">Event Timeline</h4>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {session.events.map((event, idx) => (
                                <div key={idx} className="flex gap-3 p-2 bg-zinc-800/30 rounded text-xs">
                                    <div className="w-2 h-2 mt-1 rounded-full bg-primary shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-zinc-300 font-medium">{event.event_type}</p>
                                        {event.properties?.idea && (
                                            <p className="text-zinc-500 truncate mt-1">Idea: {event.properties.idea}</p>
                                        )}
                                    </div>
                                    <span className="text-zinc-500 shrink-0">
                                        {new Date(event.timestamp).toLocaleTimeString()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : null}
        </Modal>
    );
};

// Blueprints Drill-Down Modal
export const BlueprintsModal = ({ isOpen, onClose, getAuthHeaders, initialFilter }) => {
    const [blueprints, setBlueprints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userType, setUserType] = useState(initialFilter || 'all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    const fetchBlueprints = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 15 });
            if (userType !== 'all') params.append('user_type', userType);
            if (search) params.append('search', search);
            
            const res = await axios.get(`${API}/admin/blueprints?${params}`, { headers: getAuthHeaders() });
            setBlueprints(res.data.blueprints);
            setTotalPages(res.data.total_pages);
        } catch (err) {
            toast.error('Failed to load blueprints');
        } finally {
            setLoading(false);
        }
    };
    
    const handleRemoveShared = async (blueprintId, appName) => {
        setDeleting(true);
        try {
            await axios.delete(`${API}/admin/shared-blueprints/${blueprintId}`, { headers: getAuthHeaders() });
            toast.success(`Removed "${appName}" from public sharing`);
            setDeleteConfirm(null);
            fetchBlueprints(); // Refresh list
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove shared blueprint');
        } finally {
            setDeleting(false);
        }
    };
    
    useEffect(() => {
        if (isOpen) fetchBlueprints();
    }, [isOpen, page, userType]);
    
    useEffect(() => {
        const timer = setTimeout(() => {
            if (isOpen) fetchBlueprints();
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="All Blueprints" size="lg">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                        placeholder="Search ideas or app names..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9 bg-zinc-800 border-zinc-700"
                    />
                </div>
                <Select value={userType} onValueChange={setUserType}>
                    <SelectTrigger className="w-full sm:w-36 bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="User Type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="anonymous">Anonymous</SelectItem>
                        <SelectItem value="registered">Registered</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : (
                <>
                    {blueprints.length === 0 ? (
                        <div className="text-center py-8 text-zinc-500">
                            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No blueprints found</p>
                            <p className="text-xs mt-1">Try adjusting your search or filters</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {blueprints.map((bp, idx) => (
                                <div key={idx} className="p-3 bg-zinc-800/50 rounded-lg">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-white truncate">{bp.app_name || 'Unnamed'}</p>
                                            <p className="text-xs text-zinc-400 line-clamp-2 mt-1">{bp.idea || 'No idea description'}</p>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <span className={`px-2 py-0.5 rounded text-xs ${
                                                    bp.source === 'shared' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'
                                                }`}>
                                                    {bp.source}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded text-xs ${
                                                    bp.user_type === 'registered' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                                                }`}>
                                                    {bp.user_type}
                                                </span>
                                                {bp.user_email && (
                                                    <span className="text-xs text-zinc-500">{bp.user_email}</span>
                                                )}
                                                {bp.views > 0 && (
                                                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                                                        <Eye className="w-3 h-3" /> {bp.views}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-2 shrink-0">
                                            <span className="text-xs text-zinc-500">
                                                {bp.timestamp ? new Date(bp.timestamp).toLocaleDateString() : 'N/A'}
                                            </span>
                                            {bp.source === 'shared' && (
                                                deleteConfirm === bp.id ? (
                                                    <div className="flex gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setDeleteConfirm(null)}
                                                            className="h-6 px-2 text-xs"
                                                            disabled={deleting}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            onClick={() => handleRemoveShared(bp.id, bp.app_name)}
                                                            className="h-6 px-2 text-xs"
                                                            disabled={deleting}
                                                        >
                                                            {deleting ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Confirm'}
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setDeleteConfirm(bp.id)}
                                                        className="h-6 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                                    >
                                                        <Trash2 className="w-3 h-3 mr-1" />
                                                        Unshare
                                                    </Button>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Pagination */}
                    {totalPages > 0 && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                            <span className="text-sm text-zinc-400">Page {page} of {totalPages}</span>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};


// Blueprint Reconciliation Modal - explains where numbers come from
export const ReconciliationModal = ({ isOpen, onClose, getAuthHeaders, filterMode = 'exclude_test' }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchData = async () => {
            if (!isOpen) return;
            setLoading(true);
            try {
                const res = await axios.get(`${API}/admin/blueprint-reconciliation?filter_mode=${filterMode}`, { headers: getAuthHeaders() });
                setData(res.data);
            } catch (err) {
                toast.error('Failed to load reconciliation data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [isOpen, filterMode]);
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Blueprint Metrics Reconciliation" size="lg">
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : data ? (
                <div className="space-y-6">
                    <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                        <p className="text-sm text-blue-400">
                            Filter applied: <strong>{data._filter_applied}</strong>
                        </p>
                        <p className="text-xs text-zinc-400 mt-1">
                            All numbers below use the same filter as the dashboard.
                        </p>
                    </div>
                    
                    {/* Main counts */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {Object.entries(data.counts || {}).map(([key, info]) => (
                            <div key={key} className="p-4 bg-zinc-800/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-zinc-300">{key.replace(/_/g, ' ')}</span>
                                    <span className="text-2xl font-bold text-primary">{info.value}</span>
                                </div>
                                <p className="text-xs text-zinc-500 mb-1">Source: {info.source}</p>
                                <p className="text-xs text-zinc-400">{info.explanation}</p>
                            </div>
                        ))}
                    </div>
                    
                    {/* Why numbers differ */}
                    <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                        <h4 className="text-sm font-medium text-yellow-400 mb-2">Why Numbers Differ</h4>
                        <div className="space-y-2 text-xs text-zinc-300">
                            <p><strong>Events vs Sessions:</strong> {data.why_numbers_differ?.events_vs_sessions}</p>
                            <p><strong>Events vs Persisted:</strong> {data.why_numbers_differ?.events_vs_persisted}</p>
                            <p className="text-zinc-400 mt-2">{data.why_numbers_differ?.explanation}</p>
                        </div>
                    </div>
                    
                    {/* Sample data */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-medium text-zinc-300">Sample Data</h4>
                        
                        <div>
                            <p className="text-xs text-zinc-500 mb-2">Recent Events (analytics_events)</p>
                            <div className="space-y-1">
                                {data.samples?.recent_events?.map((e, i) => (
                                    <div key={i} className="text-xs p-2 bg-zinc-800/30 rounded flex justify-between">
                                        <span className="text-zinc-300">{e.app_name || 'Unnamed'}</span>
                                        <span className="text-zinc-500">{e.session_id}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div>
                            <p className="text-xs text-zinc-500 mb-2">Saved Blueprints (saved_blueprints)</p>
                            <div className="space-y-1">
                                {data.samples?.recent_saved?.map((s, i) => (
                                    <div key={i} className="text-xs p-2 bg-zinc-800/30 rounded">
                                        <span className="text-cyan-400">{s.name}</span>
                                        <span className="text-zinc-500 ml-2">{s.idea}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div>
                            <p className="text-xs text-zinc-500 mb-2">Shared Blueprints (shared_blueprints)</p>
                            <div className="space-y-1">
                                {data.samples?.recent_shared?.map((s, i) => (
                                    <div key={i} className="text-xs p-2 bg-zinc-800/30 rounded flex justify-between">
                                        <span className="text-purple-400">{s.app_name || s.short_id}</span>
                                        <span className="text-zinc-500">{s.views} views</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </Modal>
    );
};


export default Modal;
